import React, { useMemo } from 'react';
import { PostInsights } from '@frontend/gql/social/graphql';
import { logger } from '@common';
import {
  SortingState,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { get } from 'lodash';
import PostInsightsCard from './post-insights-card';

interface PostCardViewProps {
  posts: PostInsights[];
  currentPage: number;
  pageSize: number;
  sorting?: SortingState;
  onPageChange?: (page: number) => void;
}

export const PostCardView: React.FC<PostCardViewProps> = ({
  posts,
  currentPage,
  pageSize,
  sorting = [],
  onPageChange,
}) => {
  // Create minimal column definitions for sorting
  const columns = useMemo(
    () => [
      // Use an accessor function to allow sorting on any field via the id
      {
        id: 'all-fields',
        accessorFn: (row: PostInsights) => row,
      },
      // Add individual columns for common sort fields
      ...sorting.map((sort) => ({
        id: sort.id,
        accessorFn: (row: PostInsights) => get(row, sort.id),
      })),
    ],
    [sorting],
  );

  // Initialize the table with sorting and pagination
  const table = useReactTable({
    data: posts,
    columns,
    state: {
      sorting,
      pagination: {
        pageIndex: currentPage,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      // If it's a function updater, call it with current state to get new state
      const newPaginationState =
        typeof updater === 'function' ? updater({ pageIndex: currentPage, pageSize }) : updater;

      // Notify parent component of page change
      if (onPageChange && newPaginationState.pageIndex !== currentPage) {
        onPageChange(newPaginationState.pageIndex);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
  });

  // Get the sorted and paginated data directly from the table
  const paginatedPosts = table.getPaginationRowModel().rows.map((row) => row.original);

  // Track and ensure unique keys
  const { postsWithUniqueKeys, duplicateIds } = useMemo(() => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    const result = paginatedPosts.map((post, index) => {
      if (!post.entityId) {
        // Handle posts without entityId
        return { ...post, uniqueKey: `post-${index}` };
      }

      if (seen.has(post.entityId)) {
        duplicates.add(post.entityId);
        return { ...post, uniqueKey: `${post.entityId}-${index}` };
      }

      seen.add(post.entityId);
      return { ...post, uniqueKey: post.entityId };
    });

    return { postsWithUniqueKeys: result, duplicateIds: Array.from(duplicates) };
  }, [paginatedPosts]);

  // Log duplicates if any are found
  React.useEffect(() => {
    if (duplicateIds.length > 0) {
      logger.warn('Duplicate post entityIds found:', duplicateIds);
    }
  }, [duplicateIds, paginatedPosts]);

  return (
    <div className="h-[calc(100vh-116px)] w-full overflow-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 p-4">
        {postsWithUniqueKeys.map((post) => (
          <PostInsightsCard key={post.uniqueKey} post={post} />
        ))}
      </div>
    </div>
  );
};

export default PostCardView;
